import {
    useEffect,
    useState,
    useMemo,
    CSSProperties,
    Fragment,
} from "react"
import {
    useStyles,
    CreateNewArtistcontainer,
    CreateNewArtistImage,
    CreateNewBrandLabel,
    ArtistNameBoldLabel,
} from "./styles"
import debounce from "lodash/debounce"
import { CurrentBrand, YouTubeChannel } from "modules/Types"
import { clsx, makeKey, styx } from "modules/Utils"
import useBrand from 'Hooks/useBrand'
import dayjs from "dayjs"
import CreateNewBrand from 'assets/images/CreateNewBrand.png';
import { useHistory } from "react-router-dom"
import Sty, { Container } from "modules/Sty"
import ButtonSymphony from "elements/ButtonSymphony"
import { searchBrands_api } from "api/brandApi"
import ModalSymphony from "elements/ModalSymphony"
import useScreen from "Hooks/useScreen"
import InputSymphony from "elements/InputSymphony"
import useOrganization from "Hooks/useOrganization"
import CustomAvatar from "./CustomAvatar"
import TagSymphony from "elements/TagSymphony"
import SwitchSymphony, { SwitchType } from "elements/SwitchSymphony"
import Colors from "modules/Colors"
import { PlanType } from "modules/Const"

const SEARCH_OPTIONS = [
    {
        value: 'brand',
        label: "Brand Name",
        placeholder: 'e.g. Bad Bunny, Madonna...'
    },
    {
        value: 'campaignId',
        label: "Campaign ID",
        placeholder: 'e.g. 1242'
    },

    {
        value: 'email',
        label: "User Email",
        placeholder: 'e.g. megh@integral.studio'
    },
    {
        value: 'stripe_id',
        label: "Stripe ID",
        placeholder: 'e.g. Customer ID (cus_123) or Subscription ID (sub_123)'
    },
]

const getHelperText = (args: {
    value: string
    error: boolean
    results: YouTubeChannel[]
    loading: boolean
    searchString: string
}) => {
    const { value, error, results, loading, searchString } = args

    if (error) {
        return "An error occurred. Try again later."
    }
    if (!results.length && value && !loading && value !== searchString) {
        return "Results not found."
    }
    return undefined
}

interface AdminSearchSelectorModalProps {
    className?: string;
    open: boolean
    onClose: () => void
    mode?: 'default' | 'select';
    onImportSelected?: (selectedBrands: CurrentBrand[]) => void;
}

const AdminSearchSelectorModal = ({
    className,
    mode = 'default',
    onImportSelected,
    open,
    onClose,
}: AdminSearchSelectorModalProps) => {
    const history = useHistory();
    const classes = useStyles()
    const { mobileView, cssCalc } = useScreen()
    const { setCurrentBrand } = useBrand();
    const { getOrganization } = useOrganization();

    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [searchOption, setSearchOption] = useState(SEARCH_OPTIONS[0])
    const [textFieldValue, setTextFieldValue] = useState<string>("")
    const [results, setResults] = useState<any[]>([])
    const [error, setError] = useState<boolean>(false)
    const [selectedBrands, setSelectedBrands] = useState<CurrentBrand[]>([]);

    const [selectAll, setSelectAll] = useState<boolean>(false);

    useEffect(() => {
        setError(false)
        setIsLoading(false)
    }, [])

    useEffect(() => {
        if (!textFieldValue) {
            setResults([])
            setError(false)
            setIsLoading(false)
        }
    }, [textFieldValue])

    useEffect(() => {
        setTextFieldValue('')
    }, [searchOption])

    const debouncedChangeHandler = useMemo(
        () =>
            debounce(async (value: string, searchType: string) => {
                setIsLoading(true);
                try {
                    const results = await searchBrands_api({
                        searchTerm: value,
                        type: searchType as 'brand' | 'campaignId' | 'email' | 'stripe_id'
                    });
                    setResults(results.map((brand: CurrentBrand) => ({
                        ...brand,
                        id: brand.id
                    })));
                } catch (e) {
                    setError(true);
                }
                setIsLoading(false);
            }, 800),
        []
    )

    useEffect(() => {
        return () => {
            debouncedChangeHandler.cancel()
        }
    }, [debouncedChangeHandler])

    // Handle individual brand selection
    const handleBrandSelection = (brand: CurrentBrand) => {
        if (mode === 'select') {
            const isSelected = selectedBrands.some(b => b.id === brand.id);
            if (isSelected) {
                setSelectedBrands(prev => prev.filter(b => b.id !== brand.id));
            } else {
                setSelectedBrands(prev => [...prev, brand]);
            }
        } else {
            handleOnClick(brand);
        }
    };

    // Handle select all
    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedBrands([]);
        } else {
            setSelectedBrands(results);
        }
        setSelectAll(!selectAll);
    };

    const resetSearchSelectorModal = () => {
        setSelectedBrands([])
        setResults([])
        setTextFieldValue('')
    }

    // Handle import selected
    const handleImport = () => {
        if (onImportSelected) {
            onImportSelected(selectedBrands);
            resetSearchSelectorModal()
            onClose();
        }
    };

    const renderModalFooter = () => {
        if (mode === 'select' && selectedBrands.length > 0) {
            return (
                <Container
                    className="modalFooterTeamPlan"
                    display="flex"
                    justifyContent="flex-end"
                    padding="16px"
                >
                    <ButtonSymphony
                        className="searchSelectorImportButton"
                        onClick={handleImport}
                        variant="outlined"
                    >
                        Import {selectedBrands.length} Selected
                    </ButtonSymphony>
                </Container>
            );
        }
        return null;
    };

    const showConnectButton = false

    const helperText = getHelperText({
        value: textFieldValue,
        error,
        results,
        loading: isLoading,
        searchString: textFieldValue
    })

    const handleOnClick = (brand: CurrentBrand) => {
        onClose()
        setCurrentBrand(brand)
        history.push('/dashboard')
        getOrganization(brand.organizationId)

        if (brand.organizationName) {
            getOrganization(brand.organizationId)
        }
    }

    const handleChangeTextField = (valueText: string) => {
        const value = valueText

        if (value.length && open) {
            setIsLoading(true)
            debouncedChangeHandler(value, searchOption.value)
        }

        setTextFieldValue(value)
    }

    const renderGap = () => {
        return (
            <div style={Sty.text12grey}>
                {"   "}/{"   "}
            </div>
        )
    }

    const renderBrandItemRow = (brand: CurrentBrand) => {
        const {
            name: brandName,
            image: thumbnailUrl,
            tier,
            slug,
            createdAt,
            users,
            organizationName,
            organizationId,
            id,
        } = brand;

        // Determine if the brand is selected
        const isSelected = selectedBrands.some(b => b.id === brand.id);

        const renderTier = () => {
            switch (tier) {
                case PlanType.TEAM:
                    return (
                        <div className="teamAccountLabel" style={{ ...Sty.text12, color: Colors.yellow }}>
                            TEAM ACCOUNT
                        </div>
                    )
                case PlanType.PRO:
                    return (
                        <div className="proAccountLabel" style={{ ...Sty.text12, color: Colors.yellow }}>
                            PRO ACCOUNT
                        </div>
                    )
                case PlanType.LITE:
                    return (
                        <div className="liteAccountLabel" style={{ ...Sty.text12, color: Colors.purple }}>
                            LITE ACCOUNT
                        </div>
                    )
                default:
                    return (
                        <div className="freeAccountLabel" style={{ ...Sty.text12, color: Colors.grey }}>
                            FREE ACCOUNT
                        </div>
                    )
            }
        }

        return (
            <div
                className="brandItemRow"
                style={sty.brandItemRow}
                onClick={() => {
                    if (mode === 'select' && !organizationName) {
                        return
                    } else {
                        handleBrandSelection(brand)
                    }
                }}
            >
                {/* Render checkbox if in 'select' mode */}
                {mode === 'select' && !organizationName &&
                    <SwitchSymphony
                        className="selectBrandSwitch"
                        checked={isSelected}
                        onChange={() => handleBrandSelection(brand)}
                        type={SwitchType.CHECKBOX}
                    />
                }
                <CustomAvatar
                    className="searchSelectorChannelImage"
                    image={thumbnailUrl || ''}
                    brandOverride={brand}
                    isBorder
                />
                <div className="brandItemRowContent" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {/* Brand Title and Slug */}
                    <div className="brandItemRowContentContainer" style={{ display: 'flex', flexDirection: 'column' }}>
                        <div className="brandItemRowContentTitle" style={Sty.text16bold}>
                            {brandName}
                        </div>
                        <div className="brandItemRowContentSlug" style={Sty.text16grey}>
                            (Slug: {slug} / ID: {id})
                        </div>
                    </div>

                    {/* Account Type and Creation Date */}
                    <div style={{ display: 'flex', flexDirection: 'row', gap: 4 }}>
                        {renderTier()}
                        {renderGap()}
                        <div style={Sty.text12grey}>
                            Created on {dayjs(createdAt).format('MMMM DD, YYYY')}
                        </div>
                    </div>

                    {/* Users List */}
                    <div className="usersList" style={sty.usersList}>
                        <div style={Sty.text12grey}>
                            Users:
                        </div>
                        {users?.map(({ id, email }) => (
                            <TagSymphony
                                key={makeKey('user', id)}
                                className="userTag"
                                title={email}
                                color="grey"
                            />
                        ))}
                    </div>

                    {/* Organization Name */}
                    {organizationName &&
                        <div className="organizationContainer" style={sty.organizationContainer}>
                            <div
                                style={Sty.text12grey}
                            >
                                Team Name:
                            </div>
                            <TagSymphony
                                className="organizationTag"
                                title={`${organizationName} (ID: ${organizationId})`}
                                color="grey"
                            />
                        </div>
                    }
                </div>

                {/* Connect Button */}
                {showConnectButton &&
                    <ButtonSymphony
                        className="connectButton"
                        variant="text"
                    >
                        Connect
                    </ButtonSymphony>
                }
            </div>
        );
    };

    const renderSearchBarButtons = () => {
        return (
            <div
                className="searchSelectorSearchByContainer"
                style={{ marginBottom: 20, display: 'flex', width: '100%', alignItems: 'center', gap: 8 }}
            >
                <div className="searchByTextSearch" style={Sty.text16bold}>
                    Search by:
                </div>
                {SEARCH_OPTIONS.map((option, index) => {
                    const selected = searchOption.value === option.value;
                    return (
                        <ButtonSymphony
                            key={`search-option-${option.value}-${index}`}
                            className="searchSelectorOptionButton"
                            variant={selected ? "primary" : "outlined"}
                            color={selected ? 'purple' : 'grey'}
                            size="small"
                            onClick={() => setSearchOption(option)}
                        >
                            {option.label}
                        </ButtonSymphony>
                    )
                })}
            </div>
        )
    }

    const renderSearchBarInput = () => {
        return (
            <div
                className="modalSearchSelector"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    width: '100%',
                }}
            >
                <InputSymphony
                    className={clsx("searchSelectorTextInput", classes.textField)}
                    value={textFieldValue}
                    onChange={(valueText) => handleChangeTextField(valueText)}
                    isLoading={isLoading}
                    placeholder={searchOption.placeholder}
                    helperText={helperText}
                />

                {mode === 'select' && results.length > 0 &&
                    <ButtonSymphony
                        className="searchSelectorSelectAllButton"
                        style={{
                            marginBottom: 6
                        }}
                        variant="text"
                        disabled={isLoading}
                        onClick={handleSelectAll}
                    >
                        Select All
                    </ButtonSymphony>
                }
            </div>
        )
    }

    const renderSearchResults = () => {
        return (
            <div
                className="searchResultsContainer"
                style={styx(
                    sty.searchResultsContainer,
                    { height: cssCalc(360) }
                )}
            >
                {results.length > 0 && textFieldValue !== "" && !isLoading ?
                    results.map((brand, index) => {
                        return (
                            <Fragment key={makeKey(brand.id, index)}>
                                {renderBrandItemRow(brand)}
                            </Fragment>
                        )
                    })
                    :
                    <div
                        className="searchResultsNoResultsContainer"
                        style={{
                            display: 'flex',
                            gap: 12,
                            flexDirection: 'column',
                            width: '100%',
                            padding: 16
                        }}
                    >
                        <div style={Sty.text16grey}>
                            No results found
                        </div>
                        <CreateNewArtistcontainer
                            onClick={() => history.push('/add-artist')}
                        >
                            <CreateNewArtistImage src={CreateNewBrand} />
                            <CreateNewBrandLabel>
                                Create new artist{searchOption.value === 'brand' &&
                                    <>:&nbsp;<ArtistNameBoldLabel>{textFieldValue}</ArtistNameBoldLabel></>
                                }
                            </CreateNewBrandLabel>
                        </CreateNewArtistcontainer>
                    </div>
                }
            </div>
        )
    }

    return (
        <>
        <style>
            {`
                .brandItemRow:hover {
                    background-color: ${Colors.purpleLightest};
                }
            `}
        </style>
        <ModalSymphony
            className={clsx("searchSelectorModal", className)}
            isOpen={open}
            onClose={onClose}
            fullScreen={mobileView}
            title="Admin Brand Selector"
            subtitle="Search for any brand across Symphony."
        >
            {renderSearchBarButtons()}
            {renderSearchBarInput()}

            {textFieldValue && !isLoading &&
                renderSearchResults()
            }

            {mode === 'select' && renderModalFooter()}
        </ModalSymphony>
        </>
    )
}

const sty: Record<string, CSSProperties> = {
    searchResultsContainer: {
        marginTop: 16,
        borderRadius: 8,
        boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)',
        border: Sty.border,
        overflowY: 'auto',
    },
    brandItemRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        padding: 8,
        borderBottom: Sty.border,
        cursor: 'pointer',
    },
    usersList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    organizationContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: 4,
    }
}

export default AdminSearchSelectorModal